import { useState } from 'react';

import { Flex, HStack } from '@chakra-ui/react';
import { type UseQueryResult } from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import { useDateRangeQueryState } from '@/components/Filters/Date/hooks';
import {
  RANGE_MAX_VALUE,
  RANGE_MIN_VALUE,
} from '@/components/Filters/Range/RangeFilterComponent';
import { useRangeQueryState } from '@/components/Filters/Range/hooks';
import {
  type RatingObj,
  ratingItems,
} from '@/components/Filters/Rating/RatingFilterComponent';
import {
  FullScopePagination,
  type FullScopePaginationPageSizeType,
  fullScopePaginationPageSizes,
} from '@/components/Pagination/FullScopePagination';
import { dateRangeDefaultValue } from '@/components/Popover';
import { type StatusItemsObj } from '@/features/candidate/CandidatesFilters';
import {
  type CandidateBasicDto,
  type CandidateBasicDtoPagedResponseDto,
  CandidateListType,
  CandidateSortColumn,
  CustomSortOrder,
  type SurveyParticipationSortColumn,
  type SurveyParticipationStatus,
} from '@/services/api';
import { statusFilterItems } from '@/utils/statusFilterItems';
import {
  parseAsInteger,
  parseAsJson,
  parseAsNumberLiteral,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
} from '@/utils/useQueryState';

import { useAdminSurveyPageParams } from './PageAdminSurvey.helper';
import { SurveyParticipantsList } from './SurveyParticipantsList';
import { SurveyParticipantsListHeaderSection } from './SurveyParticipantsListHeaderSection';
import { SurveyParticipantsListSearchSection } from './SurveyParticipantsListSearchSection';
import { useGetSurveyParticipantsQuery } from './useAdminSurveyQueries';

const defaultRange = [RANGE_MIN_VALUE, RANGE_MAX_VALUE];

const statusDefaultValue = statusFilterItems.reduce((a, c) => {
  a[c.name] = false;
  return a;
}, {} as StatusItemsObj);

const ratingDefaultValue = ratingItems.reduce((a, c) => {
  a[c] = false;
  return a;
}, {} as RatingObj);

export const SurveyTable = () => {
  const { surveyId } = useAdminSurveyPageParams();

  const [searchTerm, setSearchTerm] = useQueryState(
    'q',
    parseAsString.withDefault('').withOptions({ clearOnDefault: true })
  );

  const [page, setPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1).withOptions({
      clearOnDefault: true,
    })
  );

  const [pageSize, setPageSize] =
    useQueryState<FullScopePaginationPageSizeType>(
      'pageSize',
      parseAsNumberLiteral(fullScopePaginationPageSizes)
        .withDefault(10)
        .withOptions({ clearOnDefault: true })
    );

  const [sortOrder, setSortOrder] = useQueryState(
    'sortOrder',
    parseAsStringEnum<CustomSortOrder>(
      Object.values(CustomSortOrder)
    ).withOptions({ clearOnDefault: true })
  );

  const [sortItem, setSortItem] = useQueryState(
    'sortItem',
    parseAsStringEnum<CandidateSortColumn>(
      Object.values(CandidateSortColumn)
    ).withOptions({ clearOnDefault: true })
  );

  const [emailSearchTerm, setEmailSearchTerm] = useQueryState(
    'email',
    parseAsString.withDefault('').withOptions({ clearOnDefault: true })
  );

  const [dateRangeSearchTerm, setDateRangeSearchTerm] =
    useDateRangeQueryState();

  const [rangeSearchTerm, setRangeSearchTerm] =
    useRangeQueryState(defaultRange);

  const [status, setStatus] = useQueryState<StatusItemsObj>(
    'status',
    parseAsJson<StatusItemsObj>()
      .withDefault(statusDefaultValue)
      .withOptions({ clearOnDefault: true })
  );

  const [rating, setRating] = useQueryState<RatingObj>(
    'rating',
    parseAsJson<RatingObj>()
      .withDefault(ratingDefaultValue)
      .withOptions({ clearOnDefault: true })
  );

  const [candidateListType, setCandidateListType] =
    useQueryState<CandidateListType>(
      'listType',
      parseAsStringEnum<CandidateListType>(Object.values(CandidateListType))
        .withDefault(CandidateListType.AllCandidates)
        .withOptions({ clearOnDefault: true })
    );

  // Convert status filter to survey participation status array
  const selectedStatuses = Object.entries(status)
    .filter(([_, isSelected]) => isSelected)
    .map(([statusName]) => statusName as SurveyParticipationStatus);

  // Convert sort column from candidate to survey participation format
  const surveyParticipationSortColumn =
    sortItem === CandidateSortColumn.CreatedAt
      ? ('CreatedAt' as SurveyParticipationSortColumn)
      : sortItem === CandidateSortColumn.Rating
        ? ('Rating' as SurveyParticipationSortColumn)
        : undefined;

  const participantsQuery = useGetSurveyParticipantsQuery({
    surveyId: surveyId ?? '',
    q: searchTerm ?? undefined,
    status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    createdAtFrom: dateRangeSearchTerm.from
      ? new Date(dateRangeSearchTerm.from).toISOString()
      : undefined,
    createdAtTo: dateRangeSearchTerm.to
      ? new Date(dateRangeSearchTerm.to).toISOString()
      : undefined,
    sortColumn: surveyParticipationSortColumn,
    sortOrder: sortOrder ?? undefined,
    size: pageSize,
    page: page,
  });

  const [selectedCandidates, setSelectedCandidates] = useState<
    Array<CandidateBasicDto>
  >([]);

  const handleCandidateSelection = (
    candidate: CandidateBasicDto,
    isChecked: boolean
  ) => {
    setSelectedCandidates((prevSelected) => {
      const currentSelected = prevSelected ?? [];

      if (isChecked) {
        return [...currentSelected, candidate];
      } else {
        return currentSelected.filter((c) => c.id !== candidate.id);
      }
    });
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      // Note: This assumes survey participants have compatible structure with CandidateBasicDto
      setSelectedCandidates(allParticipants as Array<CandidateBasicDto>);
    } else {
      setSelectedCandidates([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedCandidates([]);
  };

  const clearAllFilters = () => {
    setEmailSearchTerm('');
    setDateRangeSearchTerm(dateRangeDefaultValue);
    setRangeSearchTerm(defaultRange);
    setStatus(statusDefaultValue);
    setRating(ratingDefaultValue);

    setSortItem(null);
    setSortOrder(null);
  };

  const totalItems: number = participantsQuery.data?.total ?? 0;
  const allParticipants = participantsQuery.data?.data ?? [];

  return (
    <Flex direction="column" borderRadius="xl" bg="white">
      <SurveyParticipantsListHeaderSection
        totalParticipants={totalItems}
        surveyId={surveyId ?? ''}
        candidateListType={candidateListType}
        setCandidateListType={setCandidateListType}
      />
      <SurveyParticipantsListSearchSection
        surveyId={surveyId ?? ''}
        selectedParticipants={selectedCandidates}
        onClearSelection={handleClearSelection}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        emailSearchTerm={emailSearchTerm}
        setEmailSearchTerm={setEmailSearchTerm}
        rangeSearchTerm={rangeSearchTerm}
        setRangeSearchTerm={setRangeSearchTerm}
        status={status}
        setStatus={setStatus}
        rating={rating}
        setRating={setRating}
        dateRangeSearchTerm={dateRangeSearchTerm}
        setDateRangeSearchTerm={setDateRangeSearchTerm}
        totalParticipants={totalItems}
        setPage={setPage}
        clearAllFilters={clearAllFilters}
      />
      <SurveyParticipantsList
        setSelectedCandidates={setSelectedCandidates}
        selectedCandidates={selectedCandidates}
        onCandidateSelect={handleCandidateSelection}
        onSelectAll={handleSelectAll}
        candidatesQuery={
          participantsQuery as unknown as UseQueryResult<
            CandidateBasicDtoPagedResponseDto,
            AxiosError
          >
        }
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        sortItem={sortItem}
        setSortItem={setSortItem}
        onDeleteSuccess={() => {
          participantsQuery.refetch();
          setSelectedCandidates([]);
        }}
      />

      <HStack py={4} px={6} justify="end">
        <FullScopePagination
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          totalItems={totalItems as number}
        />
      </HStack>
    </Flex>
  );
};
